import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Loader2, CheckCircle, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DietarySuggestionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dietary-suggestions`);
      setItems(res.data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAction = async (businessId, itemName, action) => {
    const key = `${businessId}-${itemName}`;
    setActioning(key);
    try {
      await axios.post(`${API}/admin/dietary-suggestions/${action}`, {
        business_id: businessId,
        item_name: itemName,
      });
      toast.success(`Suggestion ${action}d`);
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action}`);
    } finally { setActioning(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;

  if (items.length === 0) return <p className="text-sm text-slate-400 text-center py-6">No pending dietary suggestions</p>;

  return (
    <div className="space-y-3" data-testid="dietary-suggestions-panel">
      {items.map((item, i) => {
        const key = `${item.business_id}-${item.item_name}`;
        return (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 p-3" data-testid={`dietary-item-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-bold text-slate-800">{item.item_name}</p>
                <p className="text-[10px] text-slate-400">{item.name} ({item.business_id})</p>
              </div>
              <Sparkles className="w-4 h-4 text-emerald-400" />
            </div>

            {/* Suggestions */}
            <div className="flex flex-wrap gap-1 mb-2">
              {Object.entries(item.suggested_dietary_tags || {}).map(([tag, val]) => (
                <span key={tag} className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                  {tag.replace("is_", "").replace("_", " ")} ({Math.round((val.confidence || 0) * 100)}%)
                </span>
              ))}
            </div>

            {/* Current tags */}
            {item.current_dietary?.length > 0 && (
              <p className="text-[10px] text-slate-400 mb-2">Current: {item.current_dietary.join(", ")}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => handleAction(item.business_id, item.item_name, "approve")}
                disabled={actioning === key}
                className="flex-1 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid={`dietary-approve-${i}`}
              >
                <CheckCircle className="w-3 h-3" /> Approve
              </button>
              <button
                onClick={() => handleAction(item.business_id, item.item_name, "reject")}
                disabled={actioning === key}
                className="flex-1 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid={`dietary-reject-${i}`}
              >
                <XCircle className="w-3 h-3" /> Reject
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
